import { useState, useEffect } from 'react';
import { useTrustedDevice, TrustedDevice } from '@/hooks/useTrustedDevice';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Smartphone, Trash2, Shield, Loader2, MonitorSmartphone } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export function TrustedDevicesManager() {
  const { user } = useAuth();
  const { listMyDevices, revokeMyDevice, clearTrusted } = useTrustedDevice();
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState<string | null>(null);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    const data = await listMyDevices();
    setDevices(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const revoke = async (device: TrustedDevice) => {
    setRevoking(device.id);
    await revokeMyDevice(device.id);
    // Si c'est l'appareil courant, nettoyer localStorage
    if (user) clearTrusted(user.id);
    setDevices((prev) => prev.filter((d) => d.id !== device.id));
    toast({ title: 'Appareil révoqué', description: `${device.device_name} ne sera plus mémorisé.` });
    setRevoking(null);
  };

  const daysLeft = (expires_at: string) => {
    const diff = new Date(expires_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="h-4 w-4 animate-spin" /> Chargement des appareils...
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MonitorSmartphone className="h-5 w-5 text-primary" />
          Appareils de confiance
        </CardTitle>
        <CardDescription>
          Ces appareils ne demanderont pas le code MFA pendant la durée indiquée.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {devices.length === 0 ? (
          <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
            <Shield className="h-5 w-5" />
            Aucun appareil mémorisé. Cochez "Se souvenir de cet appareil" lors de la prochaine vérification MFA.
          </div>
        ) : (
          <div className="space-y-3">
            {devices.map((d) => {
              const days = daysLeft(d.expires_at);
              return (
                <div key={d.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Smartphone className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{d.device_name}</p>
                      {d.user_agent && (
                        <p className="text-xs text-muted-foreground/70 truncate max-w-[200px]" title={d.user_agent}>
                          {d.user_agent.length > 60 ? d.user_agent.slice(0, 57) + '…' : d.user_agent}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          Ajouté le {new Date(d.created_at).toLocaleDateString('fr-FR')}
                        </span>
                        <Badge
                          variant="outline"
                          className={`text-xs ${days <= 7 ? 'border-amber-400 text-amber-700' : 'border-green-400 text-green-700'}`}
                        >
                          {days}j restants
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive"
                    disabled={revoking === d.id}
                    onClick={() => revoke(d)}
                  >
                    {revoking === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
